/**
 * Husets mannskap og vaktplan (sak 229, V3) — `/venue-crew`.
 *
 * Alle nøklene står under `qk.venueHouse`, så en vakt som bemannes med `useSetShiftAssignees`
 * (som feier det prefikset) friskner opp ukeplanen, arrangementets vakter og husets panel på én gang.
 */
import {
  shiftDtoSchema,
  venueCrewListResponseSchema,
  venueCrewMemberDtoSchema,
  venueCrewPlanSchema,
  venueCrewPostListSchema,
  venuePostListSchema,
  type AddVenueCrewMemberRequest,
  type CreateVenuePostRequest,
  type UpdateVenuePostRequest,
  type VenuePostList,
  type CreateVenueNightShiftRequest,
  type SetShiftAssignmentStatusRequest,
  type ShiftDto,
  type UpdateVenueCrewMemberRequest,
  type VenueCrewListResponse,
  type VenueCrewMemberDto,
  type VenueCrewPlan,
  type VenueCrewPostList,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const CREW = '/venue-crew';

/** Et hus har tjue, ikke to tusen, i mannskapet: én side på hundre er hele lista. */
const PAGE_SIZE = 100;

export function venueCrewQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.venueCrewList(orgId, festivalId),
    queryFn: ({ signal }): Promise<VenueCrewListResponse> =>
      apiRequest({
        path: CREW,
        query: { festivalId, limit: PAGE_SIZE, sort: 'name', direction: 'asc' },
        schema: venueCrewListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function venueCrewPostsQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.venueCrewPosts(orgId, festivalId),
    queryFn: ({ signal }): Promise<VenueCrewPostList> =>
      apiRequest({
        path: `${CREW}/posts`,
        query: { festivalId },
        schema: venueCrewPostListSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}

export interface VenuePlanFilter {
  from?: string;
  to?: string;
  onlyGaps?: boolean;
  nightId?: string;
}

export function venueCrewPlanQueryOptions(orgId: string, festivalId: string, filter: VenuePlanFilter) {
  const query: Record<string, string> = { festivalId };
  if (filter.from !== undefined) query['from'] = filter.from;
  if (filter.to !== undefined) query['to'] = filter.to;
  if (filter.onlyGaps === true) query['onlyGaps'] = 'true';
  if (filter.nightId !== undefined) query['nightId'] = filter.nightId;
  return queryOptions({
    queryKey: qk.venueCrewPlan(orgId, festivalId, { ...filter }),
    queryFn: ({ signal }): Promise<VenueCrewPlan> =>
      apiRequest({ path: `${CREW}/shifts`, query, schema: venueCrewPlanSchema, signal }),
    ...CACHE.list,
  });
}

export function useAddVenueCrewMember(
  orgId: string,
): UseMutationResult<VenueCrewMemberDto, unknown, AddVenueCrewMemberRequest> {
  return useMutation({
    mutationFn: (body: AddVenueCrewMemberRequest) =>
      apiRequest({ path: CREW, method: 'POST', body, schema: venueCrewMemberDtoSchema }),
    meta: { invalidates: [qk.venueCrew(orgId)] },
  });
}

export interface UpdateVenueCrewMemberInput {
  id: string;
  body: UpdateVenueCrewMemberRequest;
}

export function useUpdateVenueCrewMember(
  orgId: string,
): UseMutationResult<VenueCrewMemberDto, unknown, UpdateVenueCrewMemberInput> {
  return useMutation({
    mutationFn: ({ id, body }: UpdateVenueCrewMemberInput) =>
      apiRequest({ path: `${CREW}/${id}`, method: 'PATCH', body, schema: venueCrewMemberDtoSchema }),
    meta: { invalidates: [qk.venueCrew(orgId)] },
  });
}

export function useRemoveVenueCrewMember(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (id: string) => apiRequest({ path: `${CREW}/${id}`, method: 'DELETE' }),
    meta: { invalidates: [qk.venueCrew(orgId)] },
  });
}

export interface CreateNightShiftInput {
  nightId: string;
  body: CreateVenueNightShiftRequest;
}

/** Én ekstra vakt på et arrangement. Arrangementet og planen står under huset; ressursene følger posten. */
export function useCreateVenueNightShift(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, CreateNightShiftInput> {
  return useMutation({
    mutationFn: ({ nightId, body }: CreateNightShiftInput) =>
      apiRequest({
        path: `${CREW}/nights/${nightId}/shifts`,
        method: 'POST',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.venueHouse(orgId), qk.shifts(orgId), qk.resources(orgId)] },
  });
}

export interface CrewShiftAnswer {
  shiftId: string;
  body: SetShiftAssignmentStatusRequest;
}

/**
 * Svaret til én på vakta (`PUT /shifts/:id/assignment-status`). Egen krok fordi den i
 * people-queries ikke feier huset — da ville planen stått med «Ikke svart» til neste henting.
 */
export function useSetCrewShiftStatus(
  orgId: string,
): UseMutationResult<ShiftDto, unknown, CrewShiftAnswer> {
  return useMutation({
    mutationFn: ({ shiftId, body }: CrewShiftAnswer) =>
      apiRequest({
        path: `/shifts/${shiftId}/assignment-status`,
        method: 'PUT',
        body,
        schema: shiftDtoSchema,
      }),
    meta: { invalidates: [qk.venueHouse(orgId), qk.shifts(orgId)] },
  });
}

/* ------------------------------------------------------------------- husets poster (F39) */

const POSTS = '/venue-posts';
/** En post bærer malene og vaktene: et nytt navn følger med dit, så alle tre feies. */
const postInvalidates = (orgId: string) => [qk.venueHouse(orgId), qk.shifts(orgId)];

export function venuePostsQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.venuePosts(orgId, festivalId),
    queryFn: ({ signal }): Promise<VenuePostList> =>
      apiRequest({ path: POSTS, query: { festivalId }, schema: venuePostListSchema, signal }),
    ...CACHE.list,
  });
}

export function useCreateVenuePost(
  orgId: string,
): UseMutationResult<VenuePostList, unknown, CreateVenuePostRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({ path: POSTS, method: 'POST', body, schema: venuePostListSchema }),
    meta: { invalidates: postInvalidates(orgId) },
  });
}

export function useUpdateVenuePost(
  orgId: string,
  festivalId: string,
): UseMutationResult<VenuePostList, unknown, { postId: string; body: UpdateVenuePostRequest }> {
  return useMutation({
    mutationFn: ({ postId, body }) =>
      apiRequest({
        path: `${POSTS}/${postId}`,
        method: 'PATCH',
        query: { festivalId },
        body,
        schema: venuePostListSchema,
      }),
    meta: { invalidates: postInvalidates(orgId) },
  });
}

export function useRemoveVenuePost(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (postId) => apiRequest({ path: `${POSTS}/${postId}`, method: 'DELETE' }),
    meta: { invalidates: postInvalidates(orgId) },
  });
}

export function useRestoreVenuePost(
  orgId: string,
  festivalId: string,
): UseMutationResult<VenuePostList, unknown, string> {
  return useMutation({
    mutationFn: (postId) =>
      apiRequest({
        path: `${POSTS}/${postId}/restore`,
        method: 'POST',
        query: { festivalId },
        schema: venuePostListSchema,
      }),
    meta: { invalidates: postInvalidates(orgId) },
  });
}
